import { useEffect, useState } from "react";
import { FaChartPie, FaPercentage, FaUniversity, FaChartLine } from "react-icons/fa";
import { getWeightedAverageYield, getCostOfFunds } from "../../Api/Api";
import { useWorkshop } from "../Common/WorkshopContext";
import { useNotification } from "../Notification/NotificationContext";

const EconomicUnitReport = () => {
    const { workshopId } = useWorkshop();
    const [yieldData, setYieldData] = useState(null);
    const [costOfFundsData, setCostOfFundsData] = useState(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        fetchWeightedAverageYield();
        fetchCostOfFunds();
    }, [workshopId]);

    const fetchWeightedAverageYield = async () => {
        try {
            const response = await getWeightedAverageYield(workshopId);
            setYieldData(response.data);
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const fetchCostOfFunds = async () => {
        try {
            const response = await getCostOfFunds(workshopId);
            setCostOfFundsData(response.data);
        } catch (error) {
            showNotification(error.message, "error");
        }
    };

    const netInterestMargin = (yieldData && costOfFundsData)
        ? (parseFloat(yieldData.weightedAverageYieldPercentage) - parseFloat(costOfFundsData.costOfFundsPercentage))
        : null;

    if (!yieldData || !costOfFundsData) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex items-center gap-4 mb-10">
                    <div className="p-3 bg-indigo-600 rounded-xl shadow-lg">
                        <FaChartPie className="text-white text-2xl" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Economic Unit Report</h1>
                        <p className="text-sm text-gray-500">Executive Summary of Portfolio Performance</p>
                    </div>
                </div>

                {/* Simplified Metrics Grid */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">

                        {/* Yield Row */}
                        <div className="p-8 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-indigo-100 rounded-lg">
                                    <FaChartLine className="text-indigo-600 text-lg" />
                                </div>
                                <span className="text-xs font-bold text-indigo-600 tracking-wider uppercase">Yield</span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-500 mb-1">Weighted Avg Yield</h3>
                            <p className="text-4xl font-bold text-gray-900">
                                {parseFloat(yieldData.weightedAverageYieldPercentage).toFixed(2)}%
                            </p>
                        </div>

                        {/* Cost Row */}
                        <div className="p-8 hover:bg-slate-50 transition-colors">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-emerald-100 rounded-lg">
                                    <FaUniversity className="text-emerald-600 text-lg" />
                                </div>
                                <span className="text-xs font-bold text-emerald-600 tracking-wider uppercase">Cost</span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-500 mb-1">Cost of Funds</h3>
                            <p className="text-4xl font-bold text-gray-900">
                                {parseFloat(costOfFundsData.costOfFundsPercentage).toFixed(2)}%
                            </p>
                        </div>

                        {/* Margin Row */}
                        <div className="p-8 bg-indigo-600 transition-colors">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-white/20 rounded-lg">
                                    <FaPercentage className="text-white text-lg" />
                                </div>
                                <span className="text-xs font-bold text-indigo-100 tracking-wider uppercase">Net Margin</span>
                            </div>
                            <h3 className="text-sm font-medium text-indigo-100 mb-1">Interest Margin</h3>
                            <p className="text-4xl font-bold text-white">
                                {netInterestMargin.toFixed(2)}%
                            </p>
                        </div>

                    </div>
                </div>

                {/* Footer simple indicator */}
                <div className="mt-6 flex justify-end">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                        <div className={`w-2 h-2 rounded-full ${netInterestMargin > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        System Status: Live Portfolio Data
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EconomicUnitReport;